/**
 * Remote Viewing contract (appliance tunnel ⇄ apps/cloud ⇄ viewers).
 *
 * Three parties:
 *
 *   The appliance (apps/api) keeps ONE outbound WebSocket open to the cloud at
 *   `wss://<cloud>/api/v1/tunnel`, authenticated with its licence key, while
 *   the heartbeat says remote viewing is on. Nothing listens on the plant side.
 *
 *   The cloud (apps/cloud) serves the appliance's own web app to signed-in
 *   viewers on a separate origin (the "view host", e.g. https://view.scada.ink)
 *   and forwards each request, and each Socket.IO WebSocket, through the tunnel
 *   of the appliance the viewer's session selected.
 *
 *   The portal (apps/marketing) invites viewers, lists the appliances a user
 *   may view, and launches a viewing session.
 *
 * Read-only is enforced twice with the same policy (remoteViewerRequestPolicy
 * below): once at the view host, and again on the appliance, which also
 * authenticates every forwarded request as a synthetic viewer-role identity of
 * its own minting — so a compromised cloud still cannot write a tag.
 *
 * Browser-safe: no Node types. Frames are Uint8Array/strings.
 */

import type { ApplianceConnectionStatus, RemoteViewer } from './portal';

// ── Portal routes ──────────────────────────────────────────────────────────
//
// Owner only (members and viewers get 403):
//   GET    /api/portal/remote/viewers               → RemoteViewer[] (active viewers + open invites)
//   POST   /api/portal/remote/viewers               AddRemoteViewerRequest → CreateRemoteViewerResponse (201)
//   POST   /api/portal/remote/viewers/:id/resend    (invite id) → CreateRemoteViewerResponse
//   DELETE /api/portal/remote/viewers/:id           (viewer user id or invite id) → { removed: true }
//   GET    /api/portal/remote/activity?limit=100    → RemoteViewActivity[]
//
// Any signed-in user, viewers included:
//   GET    /api/portal/remote/appliances            → RemoteApplianceSummary[]
//   POST   /api/portal/remote/sessions              CreateRemoteSessionRequest → CreateRemoteSessionResponse
//
// Public (no session; the token is the credential):
//   GET    /api/portal/remote/invite?token=…        → RemoteInvitePreview
//   POST   /api/portal/remote/invite/accept         AcceptRemoteInviteRequest → SessionInfo (sets si_session)
//
// A user with role 'viewer' is refused (403) by every other /api/portal route;
// /auth/me, /auth/logout and /auth/change-password still work for them.

/** Where the accept link points: `${PORTAL_PUBLIC_URL}${REMOTE_INVITE_ACCEPT_PATH}?token=…`. */
export const REMOTE_INVITE_ACCEPT_PATH = '/remote/invite';

export type RemoteViewerKind = 'user' | 'invite';

/**
 * active: an account user with role 'viewer'.
 * invited: an invite not yet accepted. expired: an invite past its expiry
 * (resend it, or delete it). Accepted and revoked invites are not listed.
 */
export type RemoteViewerStatus = 'active' | 'invited' | 'expired';

/** POST /api/portal/remote/viewers — and /viewers/:id/resend. */
export interface CreateRemoteViewerResponse {
  viewer: RemoteViewer;
  /**
   * The one-time accept link. Returned only here, never listed again — the
   * portal shows it so the owner can copy it. Valid for inviteTtlDays.
   */
  inviteUrl: string;
  /** True when the invite was also emailed (Resend configured on the cloud). */
  emailed: boolean;
}

/** GET /api/portal/remote/invite?token=… */
export interface RemoteInvitePreview {
  email: string;
  name: string;
  company: string;
  invitedBy: string | null;
  expiresAt: string;
}

/** POST /api/portal/remote/invite/accept */
export interface AcceptRemoteInviteRequest {
  token: string;
  /** At least 10 characters, like every portal password. */
  password: string;
  /** Optional correction of the name the owner typed. */
  name?: string;
}

export type RemoteTunnelState = 'connected' | 'disconnected';

/** GET /api/portal/remote/appliances — only appliances with remote viewing on and entitled. */
export interface RemoteApplianceSummary {
  id: string;
  name: string;
  site: string;
  /** From the heartbeat, as on the Appliances page. */
  status: ApplianceConnectionStatus;
  lastSeenAt: string | null;
  /** Whether the appliance's tunnel is open right now. Viewing needs it. */
  tunnel: RemoteTunnelState;
  tunnelConnectedAt: string | null;
  /** The launch button's state: false with a reason when it would fail. */
  viewable: boolean;
  reason: string | null;
}

/** POST /api/portal/remote/sessions */
export interface CreateRemoteSessionRequest {
  applianceId: string;
}

/**
 * Open `url` in a new tab. It is a one-time link that expires at `expiresAt`
 * (60 s); the view host exchanges it for its own session cookie (8 h).
 */
export interface CreateRemoteSessionResponse {
  url: string;
  expiresAt: string;
}

export type RemoteViewEndReason = 'signed_out' | 'expired' | 'viewer_removed' | 'access_lost' | null;

/** GET /api/portal/remote/activity — one row per viewing session, newest first. */
export interface RemoteViewActivity {
  id: string;
  userId: string | null;
  userEmail: string;
  userName: string;
  userRole: 'owner' | 'member' | 'viewer';
  applianceId: string | null;
  applianceName: string;
  startedAt: string;
  lastSeenAt: string;
  /** null while the session may still be in use. */
  endedAt: string | null;
  endReason: RemoteViewEndReason;
  /** Requests forwarded to the appliance during the session. */
  requests: number;
  ip: string | null;
  userAgent: string | null;
}

// ── View host ──────────────────────────────────────────────────────────────

/** Reserved path prefix on the view origin; never forwarded to an appliance. */
export const REMOTE_VIEW_PREFIX = '/__scada_remote';
/** httpOnly, host-only session cookie on the view origin. */
export const REMOTE_SESSION_COOKIE = 'si_view';
/**
 * Readable, host-only marker cookie ('1') set alongside si_view. It carries no
 * authority — it only tells the appliance web app it is being served remotely,
 * so it asks for REMOTE_SESSION_ENDPOINT instead of showing its login page.
 */
export const REMOTE_MARKER_COOKIE = 'si_remote';
/**
 * Answered by the appliance's tunnel service itself (never by its HTTP
 * server): `{ success, data: RemoteSessionInfo }`.
 */
export const REMOTE_SESSION_ENDPOINT = '/api/auth/remote-session';

export interface RemoteSessionInfo {
  /** The appliance's own User shape, role 'viewer', id 0. */
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: 'viewer';
    lastSignIn: string | null;
    createdAt: string;
    updatedAt: string;
  };
  /** Appliance JWT. refreshToken is always '' — ask the endpoint again instead. */
  tokens: { token: string; refreshToken: string };
  expiresInSec: number;
  remote: true;
}

// ── Tunnel ─────────────────────────────────────────────────────────────────

export const REMOTE_PROTOCOL_VERSION = 1;
export const REMOTE_TUNNEL_PATH = '/api/v1/tunnel';

/** Request headers the view host adds; the appliance trusts nothing else it sends. */
export const REMOTE_HEADERS = {
  /** Viewer's portal email — the identity the appliance mints its token for. */
  viewer: 'x-scada-remote-viewer',
  /** encodeURIComponent of the viewer's display name. */
  viewerName: 'x-scada-remote-viewer-name',
  /** View session id, so the appliance can count active viewers. */
  session: 'x-scada-remote-session',
  /** The viewer's IP as the cloud saw it. */
  clientIp: 'x-scada-remote-client-ip',
  /**
   * Which screens this viewer may open: serializeRemoteScreenScope(). Set by
   * the cloud account alone; the appliance ignores its local permissions for
   * remote viewers. Absent (an older cloud) means every screen.
   */
  screens: 'x-scada-remote-screens',
} as const;

// ── Screen scope ───────────────────────────────────────────────────────────

/**
 * 'all', or the dashboard (d), custom page (p) and report template (r) ids a
 * remote viewer may open on this appliance. Ids are the appliance's own.
 */
export type RemoteScreenScope = 'all' | { d: number[]; p: number[]; r: number[] };

/** Ids per list; the header must stay well inside a control frame. */
export const REMOTE_SCREEN_SCOPE_MAX_IDS = 2000;

const idList = (v: unknown): number[] | null => {
  if (!Array.isArray(v) || v.length > REMOTE_SCREEN_SCOPE_MAX_IDS) return null;
  const out: number[] = [];
  for (const n of v) {
    if (typeof n !== 'number' || !Number.isInteger(n) || n <= 0 || n > 0xffffffff) return null;
    out.push(n);
  }
  return [...new Set(out)].sort((a, b) => a - b);
};

/** Header/claim value → scope; null for anything malformed (callers decide the fallback). */
export function parseRemoteScreenScope(raw: unknown): RemoteScreenScope | null {
  if (raw === 'all') return 'all';
  let v: unknown = raw;
  if (typeof raw === 'string') {
    if (raw.length > 64 * 1024) return null;
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const d = idList(o.d ?? []);
  const p = idList(o.p ?? []);
  const r = idList(o.r ?? []);
  return d && p && r ? { d, p, r } : null;
}

export function serializeRemoteScreenScope(scope: RemoteScreenScope): string {
  return scope === 'all' ? 'all' : JSON.stringify({ d: scope.d, p: scope.p, r: scope.r });
}

export const REMOTE_LIMITS = {
  /** Largest request body forwarded (allow-listed POSTs are small JSON). */
  maxRequestBodyBytes: 256 * 1024,
  /** Largest response body (a generated report PDF, a capture image). */
  maxResponseBodyBytes: 64 * 1024 * 1024,
  /** Largest single WebSocket message relayed. */
  maxWsMessageBytes: 1024 * 1024,
  /** Tunnel WebSocket maxPayload: one data frame plus its 5-byte header, or a control frame. */
  maxTunnelFrameBytes: 1024 * 1024 + 1024,
  /** Largest JSON control frame. */
  maxControlFrameBytes: 64 * 1024,
  /** Open streams (HTTP + WebSocket) per tunnel; beyond this the view host answers 503. */
  maxConcurrentStreams: 256,
  /** Open streams per viewer session. */
  maxStreamsPerSession: 64,
  /** Body chunk size on the wire. */
  chunkBytes: 64 * 1024,
  /** Response bytes the appliance may send before the cloud grants more credit. */
  initialWindowBytes: 1024 * 1024,
  /** An HTTP stream with no traffic for this long is aborted. */
  httpIdleTimeoutMs: 60_000,
  /** A WebSocket stream with no traffic for this long is closed (Socket.IO pings every 25 s). */
  wsIdleTimeoutMs: 2 * 60_000,
  /** WebSocket ping on the tunnel, both directions. */
  pingIntervalMs: 25_000,
  /** No pong (or any frame) for this long and the tunnel is dropped. */
  deadAfterMs: 70_000,
  /** The appliance must send `hello` within this long of connecting. */
  helloTimeoutMs: 10_000,
  /** Above this many bytes buffered on the tunnel socket, senders pause. */
  highWaterBytes: 4 * 1024 * 1024,
  /** Reconnect backoff ceiling on the appliance. */
  maxBackoffMs: 60_000,
} as const;

/** WebSocket close codes on the tunnel. */
export const REMOTE_CLOSE = {
  normal: 1000,
  /** The appliance turned remote viewing off, or is shutting down. */
  goingAway: 1001,
  protocolError: 4002,
  /** Remote viewing is not (or no longer) on for this appliance. */
  notEntitled: 4003,
  /** A newer tunnel from the same hardware ID replaced this one. */
  replaced: 4009,
  /** No hello, or no traffic for deadAfterMs. */
  timeout: 4008,
} as const;

/**
 * JSON control frames (WebSocket text messages). Streams are opened only by
 * the cloud; `id` is a positive 32-bit integer unique within the tunnel.
 *
 * HTTP:  cloud → http-request [body frames] end
 *        appliance → http-response [body frames, each within credit] end
 *        cloud → credit (after writing received bytes to the viewer)
 *        either → abort
 * WS:    cloud → ws-open; appliance → ws-accept | ws-close
 *        either → ws-text / ws-binary data frames, ws-close
 */
export type RemoteControlFrame =
  | { t: 'hello'; v: number; softwareVersion: string }
  | { t: 'welcome'; v: number; applianceName: string | null }
  | { t: 'http-request'; id: number; method: string; path: string; headers: Record<string, string>; hasBody: boolean }
  | { t: 'http-response'; id: number; status: number; headers: Record<string, string | string[]> }
  | { t: 'end'; id: number }
  | { t: 'abort'; id: number; reason?: string }
  | { t: 'credit'; id: number; bytes: number }
  | { t: 'ws-open'; id: number; path: string; headers: Record<string, string> }
  | { t: 'ws-accept'; id: number }
  | { t: 'ws-close'; id: number; code?: number; reason?: string };

export type RemoteControlType = RemoteControlFrame['t'];

const CONTROL_TYPES = new Set<string>([
  'hello',
  'welcome',
  'http-request',
  'http-response',
  'end',
  'abort',
  'credit',
  'ws-open',
  'ws-accept',
  'ws-close',
]);

const isStreamId = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= 0xffffffff;

function isStringRecord(v: unknown): v is Record<string, string> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  return Object.values(v as Record<string, unknown>).every((x) => typeof x === 'string');
}

/** Parses and shape-checks a control frame; null for anything malformed. */
export function parseRemoteControlFrame(text: string): RemoteControlFrame | null {
  if (text.length > REMOTE_LIMITS.maxControlFrameBytes) return null;
  let f: Record<string, unknown>;
  try {
    f = JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!f || typeof f !== 'object' || typeof f.t !== 'string' || !CONTROL_TYPES.has(f.t)) return null;
  switch (f.t) {
    case 'hello':
      return typeof f.v === 'number' && typeof f.softwareVersion === 'string' ? (f as RemoteControlFrame) : null;
    case 'welcome':
      return typeof f.v === 'number' ? (f as RemoteControlFrame) : null;
    case 'http-request':
      return isStreamId(f.id) && typeof f.method === 'string' && typeof f.path === 'string' && isStringRecord(f.headers) && typeof f.hasBody === 'boolean'
        ? (f as RemoteControlFrame)
        : null;
    case 'http-response': {
      if (!isStreamId(f.id) || typeof f.status !== 'number' || !f.headers || typeof f.headers !== 'object') return null;
      const ok = Object.values(f.headers as Record<string, unknown>).every(
        (x) => typeof x === 'string' || (Array.isArray(x) && x.every((y) => typeof y === 'string')),
      );
      return ok ? (f as RemoteControlFrame) : null;
    }
    case 'credit':
      return isStreamId(f.id) && typeof f.bytes === 'number' && f.bytes > 0 ? (f as RemoteControlFrame) : null;
    case 'ws-open':
      return isStreamId(f.id) && typeof f.path === 'string' && isStringRecord(f.headers) ? (f as RemoteControlFrame) : null;
    default:
      return isStreamId(f.id) ? (f as RemoteControlFrame) : null;
  }
}

/** Binary data frames: [kind u8][stream id u32 BE][payload]. */
export const REMOTE_DATA = { body: 1, wsText: 2, wsBinary: 3 } as const;
export type RemoteDataKind = (typeof REMOTE_DATA)[keyof typeof REMOTE_DATA];

export function encodeRemoteDataFrame(kind: RemoteDataKind, id: number, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(5 + payload.byteLength);
  const view = new DataView(out.buffer);
  view.setUint8(0, kind);
  view.setUint32(1, id >>> 0, false);
  out.set(payload, 5);
  return out;
}

export function decodeRemoteDataFrame(frame: Uint8Array): { kind: RemoteDataKind; id: number; payload: Uint8Array } | null {
  if (frame.byteLength < 5) return null;
  const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  const kind = view.getUint8(0);
  if (kind !== REMOTE_DATA.body && kind !== REMOTE_DATA.wsText && kind !== REMOTE_DATA.wsBinary) return null;
  const id = view.getUint32(1, false);
  if (id === 0) return null;
  return { kind: kind as RemoteDataKind, id, payload: frame.subarray(5) };
}

// ── Read-only policy ───────────────────────────────────────────────────────

export type RemoteRequestVerdict =
  | { allowed: true; kind: 'app' | 'api' | 'socket' }
  | { allowed: false; reason: string };

/**
 * GET/HEAD API paths a remote viewer may read: what dashboards, custom pages
 * and reports render, and nothing that configures the appliance. Matched
 * against the lower-cased path (Express routes case-insensitively, so
 * `/API/Admin` would otherwise slip past a case-sensitive list).
 */
const API_READ_ALLOW: RegExp[] = [
  // Session and shell. remote-session/me/permissions are answered by the tunnel.
  /^\/api\/auth\/(remote-session|me|permissions|console)$/,
  /^\/api\/health$/,
  /^\/api\/features(\/.*)?$/,
  /^\/api\/settings\/(nav|locale|locale\/default)$/,
  /^\/api\/nav-bars(\/.*)?$/,
  // Dashboards, custom pages, reports.
  /^\/api\/dashboards(\/.*)?$/,
  /^\/api\/custom-pages(\/.*)?$/,
  /^\/api\/reports\/(templates|history)(\/[^/]+)?$/,
  /^\/api\/reports\/download\/[^/]+$/,
  /^\/api\/reports\/file\/[^/]+$/,
  /^\/api\/reports\/[^/]+\/render-data$/,
  // What their widgets bind to.
  /^\/api\/tags(\/.*)?$/,
  /^\/api\/tag-udts(\/.*)?$/,
  /^\/api\/devices(\/status\/(all|summary)|\/\d+)?$/,
  /^\/api\/historian\/(query|tags|status)$/,
  /^\/api\/data\/(tags\.json|tags\.xml|history\.json)$/,
  /^\/api\/alarms\/(active|shelves|metrics|history)$/,
  /^\/api\/(templates|style-classes|pen-sets|symbols)(\/.*)?$/,
  /^\/api\/files\/serve\/.+$/,
  /^\/api\/custom-tables(\/.*)?$/,
  /^\/api\/(oee|downtime|quality|spc|andon|work-orders|work-instructions|labor-efficiency|shifts)(\/.*)?$/,
  /^\/api\/recipes\/history$/,
  /^\/api\/sparkplug\/connections(\/[^/]+)?$/,
  /^\/api\/vision\/[^/]+\/(captures(\/[^/]+)?|stats|spc)$/,
  // The Paired Appliances widget: names, status and counts (no links for a remote viewer).
  /^\/api\/v1\/mesh\/summary$/,
];

/**
 * POSTs that only read. Each one exists because a dashboard or custom page
 * cannot render without it:
 *   - dashboard widget-query: runs the SQL stored on a saved widget through a
 *     read-only query runner; the body names a widget, never SQL.
 *   - public dashboard widget-query: the same for a public dashboard.
 *   - named query execute with trigger 'datasource': the appliance refuses
 *     write queries for that trigger, and the policy refuses any other trigger.
 *   - custom page render ticket: trades the viewer's own token for a one-use
 *     ticket the page frame loads with, so the token stays out of the URL. It
 *     grants nothing the token did not already.
 *   - report file ticket: the same, for one generated report file, so a
 *     phone can open or save it at a plain address.
 *   - Socket.IO long-polling: the fallback transport posts packets; every
 *     Socket.IO event the appliance handles is a subscription.
 */
const API_POST_ALLOW: Array<{ pattern: RegExp; body?: (b: unknown) => boolean; why: string }> = [
  { pattern: /^\/api\/dashboards\/[^/]+\/widget-query$/, why: 'dashboard SQL widget data' },
  { pattern: /^\/api\/dashboards\/public\/[^/]+\/widget-query$/, why: 'public dashboard SQL widget data' },
  { pattern: /^\/api\/custom-pages\/[^/]+\/render-ticket$/, why: 'custom page frame render ticket' },
  { pattern: /^\/api\/reports\/download\/[^/]+\/ticket$/, why: 'one-time link to a generated report file (a phone cannot open a fetched blob)' },
  {
    pattern: /^\/api\/named-queries\/[^/]+\/execute$/,
    body: (b) => !!b && typeof b === 'object' && (b as Record<string, unknown>).trigger === 'datasource',
    why: 'named query as a widget data source (read-only trigger)',
  },
];

const SOCKET_PATH = /^\/socket\.io\/?$/;

/**
 * Splits a request target into a path the allow-lists can be matched against.
 * Refuses anything whose meaning could differ between this check and the
 * appliance's router: encoded slashes or dots, backslashes, dot segments,
 * empty segments, control characters.
 */
export function normalizeRemotePath(target: string): { path: string; query: string } | null {
  if (typeof target !== 'string' || target.length === 0 || target.length > 4096 || target[0] !== '/') return null;
  const q = target.indexOf('?');
  const rawPath = q === -1 ? target : target.slice(0, q);
  const query = q === -1 ? '' : target.slice(q + 1);
  if (/[\u0000-\u001f\u007f\\#]/.test(rawPath)) return null;
  if (/%(2f|5c|2e|00)/i.test(rawPath)) return null;
  if (rawPath.startsWith('//')) return null;
  const segments = rawPath.split('/').slice(1);
  if (segments.some((s, i) => s === '.' || s === '..' || (s === '' && i !== segments.length - 1))) return null;
  return { path: rawPath.toLowerCase(), query };
}

/**
 * THE remote viewing policy: whether a viewer may make this request. Used by
 * the cloud view host, by the appliance's tunnel service before it forwards,
 * and by the appliance's HTTP guard for any request carrying a remote-viewer
 * token. `body` is the parsed JSON body (POST only).
 */
export function remoteViewerRequestPolicy(method: string, target: string, body?: unknown): RemoteRequestVerdict {
  const m = (method || '').toUpperCase();
  const norm = normalizeRemotePath(target);
  if (!norm) return { allowed: false, reason: 'That address is not allowed in remote view.' };
  const { path } = norm;

  if (SOCKET_PATH.test(path)) {
    if (m === 'GET' || m === 'HEAD' || m === 'POST') return { allowed: true, kind: 'socket' };
    return { allowed: false, reason: 'Remote view is read-only.' };
  }

  const isApi = path === '/api' || path.startsWith('/api/');
  if (!isApi) {
    // The web app's own files and client-side routes.
    if (m === 'GET' || m === 'HEAD') return { allowed: true, kind: 'app' };
    return { allowed: false, reason: 'Remote view is read-only.' };
  }

  const apiPath = path.length > 5 && path.endsWith('/') ? path.slice(0, -1) : path;
  if (m === 'GET' || m === 'HEAD') {
    if (API_READ_ALLOW.some((re) => re.test(apiPath))) return { allowed: true, kind: 'api' };
    return { allowed: false, reason: 'This part of the appliance is not available in remote view.' };
  }
  if (m === 'POST') {
    const rule = API_POST_ALLOW.find((r) => r.pattern.test(apiPath));
    if (rule && (!rule.body || rule.body(body))) return { allowed: true, kind: 'api' };
  }
  return { allowed: false, reason: 'Remote view is read-only: changes cannot be made from here.' };
}

/** Whether a WebSocket upgrade may be relayed: Socket.IO only. */
export function remoteViewerWebSocketPolicy(target: string): RemoteRequestVerdict {
  const norm = normalizeRemotePath(target);
  if (!norm || !SOCKET_PATH.test(norm.path)) return { allowed: false, reason: 'Only live data connections are relayed.' };
  return { allowed: true, kind: 'socket' };
}

/** For documentation and tests: the allow-listed POSTs and why each exists. */
export const REMOTE_POST_ALLOW_LIST: ReadonlyArray<{ pattern: string; why: string }> = [
  ...API_POST_ALLOW.map((r) => ({ pattern: r.pattern.source, why: r.why })),
  { pattern: SOCKET_PATH.source, why: 'Socket.IO long-polling transport (subscriptions only)' },
];
